import React, { forwardRef, useContext } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { css } from '@compiled/react';

import Lozenge from '@atlaskit/lozenge';
import Tooltip, { TooltipPrimitive } from '@atlaskit/tooltip';
import type { TooltipPrimitiveProps } from '@atlaskit/tooltip';
import { Flex } from '@atlaskit/primitives/compiled';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { token } from '@atlaskit/tokens';

import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { FrontCoverStateEnum } from '@confluence/route-manager/entry-points/companyHubUtils.ts';

export const i18n: {
	publishedPill: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	publishedTooltip: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	notPublishedPill: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	notPublishedTooltip: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	publishedPill: {
		id: 'company-hub-utils.hub-status-lozenge.general.dashboard.pill.published',
		defaultMessage: 'Published',
		description: 'Pill that informs the user if their hub is in a published state',
	},
	publishedTooltip: {
		id: 'company-hub-utils.hub-status-lozenge.general.dashboard.tooltip.published',
		defaultMessage: 'Your hub is visible to everyone in the company',
		description: 'Pill that informs the user if their hub is in a unpublished state',
	},
	notPublishedPill: {
		id: 'company-hub-utils.hub-status-lozenge.general.dashboard.pill.not-published',
		defaultMessage: 'Not published',
		description: 'Pill that informs the user if their hub is in a unpublished state',
	},
	notPublishedTooltip: {
		id: 'company-hub-utils.hub-status-lozenge.general.dashboard.tooltip.not-published',
		defaultMessage:
			'Your hub is only visible to you and other admin and editors. The rest of the company will not see your hub until you publish.',
		description: 'Pill that informs the user if their hub is in a unpublished state',
	},
});

const tooltipStyles = css({
	background: token('elevation.surface.overlay'),
	borderRadius: token('radius.small'),
	boxShadow: token('elevation.shadow.overlay'),
	color: token('color.text'),
	padding: `${token('space.100')} ${token('space.150')}`,
	maxWidth: '300px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
export const customTooltipContent = forwardRef<HTMLDivElement, TooltipPrimitiveProps>(
	function customTooltipContent({ children, ...props }, ref) {
		return (
			<TooltipPrimitive {...props} ref={ref} css={tooltipStyles}>
				{children}
			</TooltipPrimitive>
		);
	},
) as React.ForwardRefExoticComponent<TooltipPrimitiveProps>;

export const HubStatusLozenge = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { frontCoverState } = useContext(SPAViewContext);
	const isPublished = frontCoverState === FrontCoverStateEnum.SHOWN;

	return (
		<Flex gap="space.100" alignItems="center">
			<Lozenge
				testId={`hub-status-lozenge-${isPublished ? 'published' : 'not-published'}`}
				appearance={isPublished ? 'success' : undefined}
				isBold
			>
				<FormattedMessage {...(isPublished ? i18n.publishedPill : i18n.notPublishedPill)} />
			</Lozenge>
			<Tooltip
				component={customTooltipContent}
				position="bottom-start"
				content={formatMessage(isPublished ? i18n.publishedTooltip : i18n.notPublishedTooltip)}
			>
				<StatusInformationIcon label="" color={token('color.icon')} />
			</Tooltip>
		</Flex>
	);
};
